import { ChangeDetectorRef, Component } from '@angular/core'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/store/state/app.state'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import { Observable, Subscription } from 'rxjs'
import { GovernmentBusinessAPIModel, PaginatedAPI } from 'src/app/models/api.model'
import { governmentBusinessSummaryDetail } from 'src/app/store/selector/government-business.selector'
import { load_government_business_summary_action } from 'src/app/store/action/government-business.action'
import {
  ASSIGNED_TO_DISPLAY_ID,
  CREATED,
  DAYS_ELAPSED,
  GB_TASK_CURRENT_STATUS,
  GOVT_DEPARTMENT,
  RECEIVED_ON,
  RESPONSE_DUE_BY,
  TASK_SUB_CATEGORY,
  TASK_ID,
  TASK_NAME,
  COMPANY_ENTITY_DISPLAY
} from 'src/app/constants/ui.columns'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { DocumentService } from 'src/app/services/common/document.service'
import { GlobalConstants } from 'src/app/constants/global-constants'
import {
  getSelectedCompaniesFromState,
  getTaskCategorySubCategoryMapFromState,
  getUIStatusMappingFromState
} from '../../../store/selector/initialize.selector'
import { GOVERNMENT_BUSINESS } from 'src/app/constants/field-id-constants'
import { SAVE_SUMMARY_TABLE_SEARCH_PARAMS } from 'src/app/store/action/common/common.action'
import { SummaryTableService } from 'src/app/services/common/summary-table.service'
import { generateOptionsMapFromSet } from '../../../services/common/common.service'

@Component({
  selector: 'app-gov-business-summary',
  templateUrl: './government-business-summary.component.html',
  styleUrls: ['./government-business-summary.component.scss']
})
export class GovernmentBusinessSummaryComponent {
  governmentBusinessSummaryList$: Observable<PaginatedAPI<GovernmentBusinessAPIModel>>
  summaryOptions: SummaryDataTableOptions
  summaryData: SummaryDataTableData<GovernmentBusinessAPIModel>
  uiStatusMapping: Map<string, string>
  selectedViewForOption: any
  viewForOptions: any[]
  companyEntityMap: Map<string, string>

  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    sortBy: 'createdDatetime',
    sortOrder: 'DESC',
    mapReferenceDocuments: 'false'
  }

  searchRequestBody: any = {
    createdDatetimeRange: undefined
  }
  pageTitle = 'Government Business Summary'
  dashboardPageLink = `/${GlobalConstants.ROUTING.GOVERNMENT_BUSINESS}/${GlobalConstants.ROUTING.DASHBOARD}`
  commonSubscription: Subscription
  private taskSubCategoryMap: Map<string, string>
  readonly MODULE_NAME_GB_TASK = GlobalConstants.MODULE.GOVERNMENT_BUSINESS_TASK
  getSelectedCompaniesFromState$: Subscription
  isVisible: boolean = true

  constructor(
    private appState: Store<AppState>,
    private documentService: DocumentService,
    private cdr: ChangeDetectorRef,
    private readonly summaryTableService: SummaryTableService
  ) {
    this.governmentBusinessSummaryList$ = this.appState.select(governmentBusinessSummaryDetail())
    this.summaryOptions = {
      downloadable: true,
      additionalButtons: false,
      contentSelectable: true,
      contentSelector: true,
      filterable: true,
      formattedContent: false,
      pageable: false,
      sortable: true,
      quickFilters: true
    }
  }

  ngOnInit(): void {
    this.appState.select(getUIStatusMappingFromState).subscribe(uiStatusMapping => {
      if (uiStatusMapping) {
        this.uiStatusMapping = uiStatusMapping
      }
    })
    this.appState
      .select(getTaskCategorySubCategoryMapFromState(this.MODULE_NAME_GB_TASK))
      .subscribe(categorySubCategoryMap => {
        if (categorySubCategoryMap) {
          const subCategoriesArr = Array.from(categorySubCategoryMap?.values()).flatMap(array => array)
          const uniqueSubCategories = new Set(subCategoriesArr)
          this.taskSubCategoryMap = generateOptionsMapFromSet(uniqueSubCategories)
        }
      })
    this.getSelectedCompaniesFromState$ = this.appState
      .select(getSelectedCompaniesFromState)
      .subscribe(selectedCompanies => {
        if (selectedCompanies) {
          const filteredCompanies = selectedCompanies.filter(sc => sc.type === GlobalConstants.COMPANY_TYPE.ENTITY)
          this.companyEntityMap = new Map<string, string>()
          filteredCompanies?.forEach(company => {
            this.companyEntityMap.set(company.id, company.displayCode)
          })
        }
      })
    this.viewForOptions = this.summaryTableService.buildViewForOption()
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = { ...this.buildCreatedatetimeFilter(firstViewForOption) }
    this.summaryData = {
      content$: this.governmentBusinessSummaryList$,
      contentSelector: {
        label: 'View for',
        selectedValue: this.selectedViewForOption,
        selectionOptions: this.viewForOptions,
        onChange: this.onViewForChange
      },
      headers: [
        TASK_ID,
        TASK_NAME,
        GOVT_DEPARTMENT,
        RECEIVED_ON,
        DAYS_ELAPSED,
        RESPONSE_DUE_BY,
        GB_TASK_CURRENT_STATUS,
        CREATED,
        ASSIGNED_TO_DISPLAY_ID
      ],
      formattingLegend: [
        {
          formatLabel: 'High Priority',
          formatClassName: 'priority-high'
        },
        {
          formatLabel: 'Medium Priority',
          formatClassName: 'priority-medium'
        },
        {
          formatLabel: 'Low Priority',
          formatClassName: 'priority-low'
        }
      ],
      searchCallback: this.searchCallback,
      rowFormatting: this.rowFormattingForDaysElapsed,
      sortCallback: this.sortCallback,
      selectPageCallback: this.selectPageCallback,
      additionalButtons: [],
      currentSearchParams: this.queryParamValues,
      checkBoxSelectionIds: [],
      onDropdownLimitChange: this.onDropdownLimitChange,
      downloadDocumentCallback: this.downloadDocumentCallback
    }
    this.addFiltersWithDynamicOptions()

    this.appState
      .select('common')
      .subscribe(data => {
        const searchParams = data?.summaryTableData?.get(GOVERNMENT_BUSINESS)
        if (searchParams) {
          const status = searchParams?.searchRequestBody?.statusList?.[0]
          const parentStatus = this.uiStatusMapping?.[status]
          let searchRequestBody = searchParams?.searchRequestBody
          if (parentStatus) {
            searchRequestBody = { ...searchRequestBody, statusList: parentStatus }
          }
          this.searchRequestBody = {
            ...this.searchRequestBody,
            createdDatetimeRange: searchParams.searchRequestBody?.createdDatetimeRange
          }
          const contentSelector = {
            ...this.summaryData.contentSelector,
            selectedValue: searchParams.selectedViewForOption
          }
          this.summaryData = { ...this.summaryData, appliedFilters: searchRequestBody, contentSelector }
          this.appState.dispatch(load_government_business_summary_action(searchParams))
        } else {
          this.loadSummaryData()
        }
      })
      .unsubscribe()
  }

  selectPageCallback = pageNum => {
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  onViewForChange = $event => {
    if ($event.target.value != 'undefined') {
      this.selectedViewForOption = $event.target.value

      const searchRequestBody = { ...this.searchRequestBody, ...this.buildCreatedatetimeFilter($event.target.value) }
      this.searchRequestBody = searchRequestBody
      this.queryParamValues.sortBy = 'createdDatetime'
      this.queryParamValues.sortOrder = 'DESC'
    } else {
      const searchRequestBody = { ...this.searchRequestBody, createdDatetimeRange: undefined }
      this.searchRequestBody = searchRequestBody
      this.selectedViewForOption = null
    }
    this.loadSummaryData()
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  back() {}

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      },
      {
        key: 'mapReferenceDocuments',
        value: this.queryParamValues.mapReferenceDocuments
      }
    ]
  }

  private loadSummaryData() {
    const options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(load_government_business_summary_action(options))
    this.appState.dispatch(
      SAVE_SUMMARY_TABLE_SEARCH_PARAMS({
        owner: GOVERNMENT_BUSINESS,
        data: { ...options, selectedViewForOption: this.selectedViewForOption }
      })
    )
  }

  //TODO: refactor code specially for reset
  searchCallback = (searchParams, button, reset: boolean) => {
    let selectedStatus = []
    if (searchParams.statusList) {
      let selected = searchParams.statusList
      for (let [key, value] of Object.entries(this.uiStatusMapping)) {
        if (selected === value) {
          selectedStatus.push(key)
        }
      }
    }
    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some(value => value !== undefined && value !== '')) {
      searchParams = {}
    }

    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams,
      statusList: selectedStatus,
      ...this.buildCreatedatetimeFilter(this.selectedViewForOption)
    }
    if (reset) {
      this.resetFilters()
    }
    this.loadSummaryData()
  }

  rowFormattingForDaysElapsed = (record: GovernmentBusinessAPIModel): string => {
    if (record.daysElapsed > 7) {
      return 'priority-high'
    } else if (record.daysElapsed > 0 && record.daysElapsed <= 7) {
      return 'priority-medium'
    } else {
      return 'priority-low'
    }
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  private downloadDocumentCallback = (documentType: string) => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${ServiceUrlConstants.API_GOVERNMENT_BUSINESS_SUMMARY_LIST_EXPORT}${documentType}`

    const options = {
      queryParams: this.buildQueryParams(true).filter(param => param.key !== 'mapReferenceDocuments'),
      requestBody: this.searchRequestBody
    }
    this.documentService.downloadDocument(endPoint, options)
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }

  addFiltersWithDynamicOptions() {
    COMPANY_ENTITY_DISPLAY.filter.getOptionsList = () => {
      return this.companyEntityMap
    }
    COMPANY_ENTITY_DISPLAY.build = (data: any) => {
      return this.companyEntityMap?.get(data?.[COMPANY_ENTITY_DISPLAY.key])
    }
    this.summaryData?.headers?.splice(0, 0, COMPANY_ENTITY_DISPLAY)

    // Task Category
    TASK_SUB_CATEGORY.filter.getOptionsList = () => {
      return this.taskSubCategoryMap
    }
    this.summaryData?.headers?.splice(1, 0, TASK_SUB_CATEGORY)
  }

  ngOnDestroy(): void {
    this?.commonSubscription?.unsubscribe()
    this?.getSelectedCompaniesFromState$?.unsubscribe()
  }

  buildCreatedatetimeFilter(value: string) {
    if (!value) {
      return null
    }
    const daterange = value.split('^')
    return { createdDatetimeRange: { begin: new Date(daterange[0]), end: new Date(daterange[1]) } }
  }

  resetFilters() {
    this.isVisible = false
    const firstViewForOption = this.viewForOptions[0].key
    this.selectedViewForOption = firstViewForOption
    this.searchRequestBody = {
      ...this.searchRequestBody,
      ...this.summaryTableService.buildEmptyCreatedatetimeFilterIfValueIsNull(firstViewForOption)
    }
    this.queryParamValues = {
      pageNo: 0,
      pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
      sortBy: 'createdDatetime',
      sortOrder: 'DESC',
      mapReferenceDocuments: 'false'
    }
    this.summaryData.contentSelector.selectedValue = this.selectedViewForOption
    this.cdr.detectChanges()
    this.isVisible = true
  }
}
